import React, {useState} from 'react';

import AemComponent from '@/components/Aem/AemComponent/AemComponent';
import EditableText from '@/components/Aem/EditableText/EditableText';
import {AppButton} from '@/components/Button';
import DeviceIcon from '@/components/DeviceIcon/DeviceIcon';
import {StickyHeader} from '@/components/Layout';
import * as advanceDiagnosticScreenTypes from '@/consts/advanceDiagnostic/advanceDiagnosticScreenTypes';
import {
    AD_POST_CLEANING_DEVICE_HEADER_TITLE,
    AD_POST_CLEANING_HEADER_TITLE,
    ADVANCED_DIAGNOSTICS_POST_CLEANING_DEVICE_NO_BUTTON_ARIA_LABEL,
    ADVANCED_DIAGNOSTICS_POST_CLEANING_DEVICE_YES_BUTTON_ARIA_LABEL,
    ADVANCED_DIAGNOSTICS_POST_CLEANING_NO_BUTTON_ARIA_LABEL,
    ADVANCED_DIAGNOSTICS_POST_CLEANING_REPEAT_BUTTON_ARIA_LABEL,
    ADVANCED_DIAGNOSTICS_POST_CLEANING_YES_BUTTON_ARIA_LABEL,
} from '@/consts/localization/localizationKeys';
import routePaths from '@/consts/route/routePaths';
import useIsMobileLayout from '@/hooks/layout/useIsMobileLayout';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import {getAriaLabel} from '@/services/accessibilityService';
import {getLocalizedStrings} from '@/services/localization/localizationService';
import appRouterService from '@/services/route/appRouterService';

import AdvancedDiagnosticDescription from '../components/AdvancedDiagnosticDescription';
import AdvancedDiagnosticLayout from '../components/AdvancedDiagnosticLayout';
import AdvancedDiagnosticTitle from '../components/AdvancedDiagnosticTitle';
import useRedirectToPhase2 from '../hooks/useRedirectToPhase2';

const AdvancedDiagnosticPostCleaning = () => {
    const isMobile = useIsMobileLayout();
    const [isResultScreen, setIsResultScreen] = useState(false);
    const redirectToPhase2 = useRedirectToPhase2({disabled: !isResultScreen});
    const localizedStrings = getLocalizedStrings();

    const onNextClick = () => {
        if (isResultScreen) {
            appRouterService.forwardToAdvancedDiagnosticScreen({
                screenName: advanceDiagnosticScreenTypes.FINISH_SCREEN,
            });
        } else {
            setIsResultScreen(true);
        }
    };

    const onDeclineClick = () => {
        if (isResultScreen) {
            redirectToPhase2();
        } else {
            appRouterService.forwardToDevicesPage();
        }
    };

    const forwardToCleaningGuide = () => {
        appRouterService.forwardToCleaningGuide({isAdvancedDiagnostic: true});
    };

    const onBackClick = () => {
        setIsResultScreen(false);
    };

    const screenProps = {onNextClick, onDeclineClick};

    useDocumentTitle(
        isResultScreen
            ? localizedStrings[AD_POST_CLEANING_HEADER_TITLE]
            : localizedStrings[AD_POST_CLEANING_DEVICE_HEADER_TITLE]
    );

    return isResultScreen ? (
        <ScreenComponent
            title='postcleaningresulttitle'
            acceptButtonText='postcleaningyesbutton'
            acceptButtonAriaLabelKey={ADVANCED_DIAGNOSTICS_POST_CLEANING_YES_BUTTON_ARIA_LABEL}
            declineButtonText='postcleaningnobutton'
            declineButtonAriaLabelKey={ADVANCED_DIAGNOSTICS_POST_CLEANING_NO_BUTTON_ARIA_LABEL}
            {...screenProps}
        >
            {isMobile && <StickyHeader onBack={onBackClick} title={localizedStrings[AD_POST_CLEANING_HEADER_TITLE]} />}
            <AppButton
                tertiary
                onClick={forwardToCleaningGuide}
                aria-label={getAriaLabel(ADVANCED_DIAGNOSTICS_POST_CLEANING_REPEAT_BUTTON_ARIA_LABEL)}
            >
                <AemComponent
                    component={EditableText}
                    pageName={routePaths.ADVANCE_DIAGNOSTIC}
                    itemName='postcleaningrepeatbutton'
                />
            </AppButton>
        </ScreenComponent>
    ) : (
        <ScreenComponent
            title='postcleaningdevicetitle'
            description='postcleaningdevicedesc'
            acceptButtonText='postcleaningdeviceyesbutton'
            acceptButtonAriaLabelKey={ADVANCED_DIAGNOSTICS_POST_CLEANING_DEVICE_YES_BUTTON_ARIA_LABEL}
            declineButtonText='postcleaningdevicenobutton'
            declineButtonAriaLabelKey={ADVANCED_DIAGNOSTICS_POST_CLEANING_DEVICE_NO_BUTTON_ARIA_LABEL}
            {...screenProps}
        >
            {isMobile && <StickyHeader title={localizedStrings[AD_POST_CLEANING_DEVICE_HEADER_TITLE]} />}
        </ScreenComponent>
    );
};

const ScreenComponent = ({
    title,
    description,
    acceptButtonText,
    acceptButtonAriaLabelKey,
    declineButtonText,
    declineButtonAriaLabelKey,
    onNextClick,
    onDeclineClick,
    children,
}) => {
    const buttonClassName = 'mt-0 mb-3 w-full';

    return (
        <AdvancedDiagnosticLayout>
            <AdvancedDiagnosticLayout.ContentWrapper>
                <AdvancedDiagnosticLayout.Container>
                    <div className='flex h-full flex-col items-center justify-center ml:h-auto'>
                        <DeviceIcon mode='diagnostics' className='h-16 w-16' />
                        <AdvancedDiagnosticTitle itemName={title} />
                        <AdvancedDiagnosticDescription itemName={description} />
                    </div>

                    <div className='mt-10 w-full'>
                        <AppButton
                            primary
                            light
                            className={buttonClassName}
                            onClick={onNextClick}
                            aria-label={getAriaLabel(acceptButtonAriaLabelKey)}
                        >
                            <AemComponent
                                component={EditableText}
                                pageName={routePaths.ADVANCE_DIAGNOSTIC}
                                itemName={acceptButtonText}
                            />
                        </AppButton>
                        <AppButton
                            secondary
                            light
                            className={buttonClassName}
                            onClick={onDeclineClick}
                            aria-label={getAriaLabel(declineButtonAriaLabelKey)}
                        >
                            <AemComponent
                                component={EditableText}
                                pageName={routePaths.ADVANCE_DIAGNOSTIC}
                                itemName={declineButtonText}
                            />
                        </AppButton>
                    </div>
                    {children}
                </AdvancedDiagnosticLayout.Container>
            </AdvancedDiagnosticLayout.ContentWrapper>
        </AdvancedDiagnosticLayout>
    );
};

export default AdvancedDiagnosticPostCleaning;
